import React, { useEffect, useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Table, Input, DatePicker, Drawer } from "antd";
import { CircularProgress } from "@mui/material";
import { format } from "date-fns";
import {
  Currency,
  SettlementStatus,
  callNonStoreApi,
  formatPrice,
  isNullOrUndefined,
} from "../utils/utils";
import {
  GetGrowezyClientSettlementDetails,
  SendSettlementCommunication,
  GetOrders,
} from "../utils/ApiEndpoints";
import { enqueueSnackbar } from "notistack";
import { Parser } from "@json2csv/plainjs";
import { Download, ContentCopy, Send, Check, Close } from "@mui/icons-material";

const { RangePicker } = DatePicker;

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Design tokens
const colors = {
  bg: "#fafbfc",
  surface: "#ffffff",
  surfaceHover: "#f8fafc",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  text: "#0f172a",
  textSecondary: "#475569",
  textMuted: "#94a3b8",
  accent: "#6366f1",
  accentLight: "#eef2ff",
  success: "#059669",
  successBg: "#ecfdf5",
  successBorder: "#a7f3d0",
};

const PageContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${colors.bg};
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.border};
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${colors.successBg};
  border: 1px solid ${colors.successBorder};
  border-radius: 8px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.success};
`;

const PageTitle = styled.h1`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
  letter-spacing: -0.01em;
`;

const StatsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const StatLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 52px;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.borderLight};
  flex-shrink: 0;
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.textSecondary};
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchInput = styled(Input)`
  width: 200px;

  &.ant-input-affix-wrapper {
    background: ${colors.bg};
    border-radius: 8px;
    border: 1px solid ${colors.border};
    padding: 8px 12px;
    font-size: 13px;
    transition: all 0.15s ease;

    &:hover {
      border-color: ${colors.textMuted};
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${colors.accent};
      box-shadow: 0 0 0 3px ${colors.accentLight};
    }

    .ant-input {
      background: transparent;
      font-size: 13px;

      &::placeholder {
        color: ${colors.textMuted};
      }
    }
  }
`;

const StyledRangePicker = styled(RangePicker)`
  &.ant-picker {
    background: ${colors.bg};
    border-radius: 8px;
    border: 1px solid ${colors.border};
    padding: 6px 12px;
    transition: all 0.15s ease;

    &:hover {
      border-color: ${colors.textMuted};
    }

    &.ant-picker-focused {
      border-color: ${colors.accent};
      box-shadow: 0 0 0 3px ${colors.accentLight};
    }
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease;

  .ant-table-wrapper,
  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table {
    height: 100%;
  }

  .ant-table-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-table-header {
    flex-shrink: 0;
  }

  .ant-table-body {
    flex: 1;
    overflow-y: auto !important;
  }

  .ant-table-thead > tr > th {
    background: ${colors.surface};
    color: ${colors.textMuted};
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 14px 32px;
    border-bottom: 1px solid ${colors.border};

    &::before {
      display: none !important;
    }

    &.ant-table-column-has-sorters:hover {
      background: ${colors.surfaceHover};
    }
  }

  .ant-table-tbody > tr {
    transition: background 0.1s ease;
    cursor: pointer;

    &:hover > td {
      background: ${colors.surfaceHover} !important;
    }

    > td {
      padding: 16px 32px;
      color: ${colors.text};
      border-bottom: 1px solid ${colors.borderLight};
      font-size: 13px;
      vertical-align: middle;
    }

    &:last-child > td {
      border-bottom: none;
    }
  }

  .ant-pagination {
    padding: 16px 32px;
    margin: 0;
    background: ${colors.surface};
    border-top: 1px solid ${colors.border};
  }
`;

const ClientName = styled.span`
  font-weight: 500;
  color: ${colors.text};
`;

const IdText = styled.span`
  font-family: "SF Mono", Monaco, "Consolas", monospace;
  font-size: 12px;
  color: ${colors.textSecondary};
`;

const DateText = styled.span`
  color: ${colors.textSecondary};
`;

const OrdersCount = styled.span`
  color: ${colors.textSecondary};
  font-size: 13px;
`;

const Amount = styled.span`
  font-weight: 600;
  color: ${colors.success};
  font-variant-numeric: tabular-nums;
  font-size: 13px;
`;

const UtrText = styled.span`
  font-family: "SF Mono", Monaco, "Consolas", monospace;
  font-size: 12px;
  color: ${colors.textSecondary};
`;

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke={colors.textMuted}
    strokeWidth="1.5"
  >
    <circle cx="7" cy="7" r="5" />
    <path d="M11 11l3.5 3.5" />
  </svg>
);

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: ${colors.textMuted};
  font-size: 13px;
  font-weight: 500;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 2px solid ${colors.borderLight};
  border-top-color: ${colors.accent};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const EmptyContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${colors.textMuted};
`;

const EmptyText = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const EmptySubtext = styled.span`
  font-size: 13px;
  color: ${colors.textMuted};
`;

// Drawer styles
const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.border};
`;

const DrawerTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.textMuted};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.borderLight};
    color: ${colors.text};
  }
`;

const DrawerContent = styled.div`
  padding: 24px;
`;

const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: ${colors.successBg};
  border: 1px solid ${colors.successBorder};
  border-radius: 10px;
  margin-bottom: 24px;
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${colors.success};
  border-radius: 50%;
  color: white;
`;

const StatusText = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.success};
`;

const StatusSub = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  margin-top: 2px;
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  color: ${colors.textMuted};
`;

const DetailValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text};
`;

const DetailValueMono = styled.span`
  font-family: "SF Mono", Monaco, "Consolas", monospace;
  font-size: 13px;
  color: ${colors.text};
`;

const AmountCard = styled.div`
  padding: 16px;
  background: ${colors.bg};
  border: 1px solid ${colors.border};
  border-radius: 10px;
`;

const AmountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.borderLight};
  }
`;

const AmountLabel = styled.span`
  font-size: 13px;
  color: ${colors.textSecondary};
`;

const AmountValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text};
  font-variant-numeric: tabular-nums;
`;

const AmountValueHighlight = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.success};
  font-variant-numeric: tabular-nums;
`;

const DrawerActions = styled.div`
  display: flex;
  gap: 10px;
  padding: 20px 24px;
  border-top: 1px solid ${colors.border};
  background: ${colors.surface};
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${(props) => (props.$primary ? colors.text : colors.surface)};
  color: ${(props) => (props.$primary ? "white" : colors.text)};
  border: 1px solid ${(props) => (props.$primary ? colors.text : colors.border)};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${(props) => (props.$primary ? "#000" : colors.surfaceHover)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: 11px;
  color: ${colors.textMuted};
  cursor: pointer;
  margin-left: 8px;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.accentLight};
    border-color: ${colors.accent};
    color: ${colors.accent};
  }
`;

function SettlementsSettled() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchData = async () => {
    try {
      const url = `${GetGrowezyClientSettlementDetails}?status=Settled`;
      const res = await callNonStoreApi(url);
      setData(res.data.docs || []);
    } catch {
      enqueueSnackbar("Failed to load", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRowClick = (record) => {
    setSelectedSettlement(record);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedSettlement(null), 300);
  };

  const handleDownloadCSV = async (record, e) => {
    if (e) e.stopPropagation();
    setDownloadingId(record.settlementId);
    try {
      const response = await callNonStoreApi(GetOrders, {
        ordersList: record.ordersList,
        userId: record.userId,
        settlementId: record.settlementId,
      });
      const orders = response.data.orders || [];
      if (!orders.length) {
        enqueueSnackbar("No orders", { variant: "warning" });
        return;
      }

      const list = orders.map((d) => ({
        Customer: d.customerName,
        Offering: d.offeringName,
        "Order Id": d.orderId,
        Date: format(new Date(d.date), "dd MMM yyyy, HH:mm"),
        Amount: formatPrice(
          d.paymentBreakdown?.totalAmount || 0,
          Currency.INR,
          "code"
        ),
        Receivable: formatPrice(d.payoutAmount || 0, Currency.INR, "code"),
        TDS: `${(record.tdsPercent || 0) * 100}%`,
        "After TDS": formatPrice(
          (d.payoutAmount || 0) * (1 - (record.tdsPercent || 0)),
          Currency.INR,
          "code"
        ),
      }));

      const csv = new Parser().parse(list);
      const blob = new Blob([csv], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${record.settlementId}_${
        record.brandPage?.name ?? record.username
      }.csv`;
      link.click();
      enqueueSnackbar("Downloaded", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed", { variant: "error" });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCopy = async (text, e) => {
    if (e) e.stopPropagation();
    await navigator.clipboard.writeText(text);
    enqueueSnackbar("Copied", { variant: "success" });
  };

  const handleResend = async (id, e) => {
    if (e) e.stopPropagation();
    setSendingId(id);
    try {
      await callNonStoreApi(SendSettlementCommunication, { settlementId: id });
      enqueueSnackbar("Notification sent", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed", { variant: "error" });
    } finally {
      setSendingId(null);
    }
  };

  const filteredData = useMemo(() => {
    let result = data;
    if (searchText.trim()) {
      const s = searchText.toLowerCase();
      result = result.filter((i) => {
        const displayName = i.brandPage?.name ?? i.username;
        return (
          displayName?.toLowerCase().includes(s) ||
          i.settlementId?.toLowerCase().includes(s) ||
          i.utr?.toLowerCase().includes(s)
        );
      });
    }
    if (dateRange?.[0] && dateRange?.[1]) {
      const start = dateRange[0].startOf("day").toDate();
      const end = dateRange[1].endOf("day").toDate();
      result = result.filter(
        (i) =>
          i.settlementDate &&
          new Date(i.settlementDate) >= start &&
          new Date(i.settlementDate) <= end
      );
    }
    return result;
  }, [data, searchText, dateRange]);

  const stats = useMemo(
    () =>
      filteredData.reduce(
        (a, i) => ({
          settlements: a.settlements + 1,
          orders: a.orders + (i.orders?.count ?? i.orders?.list?.length ?? 0),
          amount:
            a.amount +
            (i.orders?.totalAmountAfterTds ??
              i.orders?.totalPayoutAfterTds ??
              0),
          tds: a.tds + (i.orders?.tds || 0),
        }),
        { settlements: 0, orders: 0, amount: 0, tds: 0 }
      ),
    [filteredData]
  );

  const getDisplayName = (r) => r.brandPage?.name ?? r.username;

  const columns = [
    {
      title: "Client",
      width: 160,
      render: (_, r) => <ClientName>{getDisplayName(r)}</ClientName>,
      sorter: (a, b) =>
        (getDisplayName(a) ?? "").localeCompare(getDisplayName(b) ?? ""),
    },
    {
      title: "Settlement ID",
      dataIndex: "settlementId",
      width: 160,
      render: (id) => <IdText>{id}</IdText>,
    },
    {
      title: "UTR",
      dataIndex: "utr",
      width: 160,
      render: (u) => <UtrText>{isNullOrUndefined(u) ? "—" : u}</UtrText>,
    },
    {
      title: "Orders",
      dataIndex: "orders",
      width: 100,
      render: (o) => (
        <OrdersCount>{o?.count ?? o?.list?.length ?? 0} orders</OrdersCount>
      ),
    },
    {
      title: "Amount",
      dataIndex: "orders",
      width: 140,
      render: (o) => (
        <Amount>
          {formatPrice(
            o?.totalAmountAfterTds ?? o?.totalPayoutAfterTds ?? 0,
            Currency.INR
          )}
        </Amount>
      ),
    },
    {
      title: "Date",
      dataIndex: "settlementDate",
      width: 120,
      render: (d) => (
        <DateText>
          {isNullOrUndefined(d) ? "—" : format(new Date(d), "MMM d, yyyy")}
        </DateText>
      ),
      sorter: (a, b) =>
        new Date(a.settlementDate || 0) - new Date(b.settlementDate || 0),
      defaultSortOrder: "descend",
    },
  ];

  if (loading)
    return (
      <PageContainer>
        <Header>
          <HeaderLeft>
            <StatusIndicator>
              <StatusDot />
            </StatusIndicator>
            <PageTitle>Settled Transactions</PageTitle>
          </HeaderLeft>
        </Header>
        <LoadingContainer>
          <Spinner />
          <span>Loading settlements...</span>
        </LoadingContainer>
      </PageContainer>
    );

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <StatusIndicator>
            <StatusDot />
          </StatusIndicator>
          <PageTitle>Settled Transactions</PageTitle>
        </HeaderLeft>
        <StatsGroup>
          <StatItem>
            <StatLabel>Settled</StatLabel>
            <StatValue>{stats.settlements}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Orders</StatLabel>
            <StatValue>{stats.orders}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Paid</StatLabel>
            <StatValue>{formatPrice(stats.amount, Currency.INR)}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>TDS</StatLabel>
            <StatValue>{formatPrice(stats.tds, Currency.INR)}</StatValue>
          </StatItem>
        </StatsGroup>
      </Header>
      <Toolbar>
        <RecordCount>
          {filteredData.length}{" "}
          {filteredData.length === 1 ? "settlement" : "settlements"}
        </RecordCount>
        <ToolbarRight>
          <StyledRangePicker
            onChange={setDateRange}
            placeholder={["From", "To"]}
            size="small"
          />
          <SearchInput
            placeholder="Search..."
            prefix={<SearchIcon />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </ToolbarRight>
      </Toolbar>
      {filteredData.length === 0 ? (
        <EmptyContainer>
          <EmptyText>
            {searchText || dateRange
              ? "No matching settlements"
              : "No settled transactions"}
          </EmptyText>
          <EmptySubtext>
            {searchText || dateRange
              ? "Try different filters"
              : "Completed settlements will appear here"}
          </EmptySubtext>
        </EmptyContainer>
      ) : (
        <TableContainer>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="settlementId"
            pagination={{ pageSize: 20, showSizeChanger: false, size: "small" }}
            scroll={{ y: "calc(100vh - 170px)" }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
          />
        </TableContainer>
      )}

      <Drawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        width={420}
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        {selectedSettlement && (
          <>
            <DrawerHeader>
              <DrawerTitle>Settlement Details</DrawerTitle>
              <CloseBtn onClick={handleCloseDrawer}>
                <Close sx={{ fontSize: 20 }} />
              </CloseBtn>
            </DrawerHeader>

            <DrawerContent>
              <StatusBanner>
                <StatusIcon>
                  <Check sx={{ fontSize: 16 }} />
                </StatusIcon>
                <StatusText>
                  <StatusTitle>Settlement Successful</StatusTitle>
                  <StatusSub>
                    {selectedSettlement.settlementDate
                      ? format(
                          new Date(selectedSettlement.settlementDate),
                          "MMMM d, yyyy 'at' h:mm a"
                        )
                      : "—"}
                  </StatusSub>
                </StatusText>
              </StatusBanner>

              <DetailSection>
                <SectionTitle>Client Information</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Client Name</DetailLabel>
                    <DetailValue>
                      {selectedSettlement.brandPage?.name ??
                        selectedSettlement.username}
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Orders</DetailLabel>
                    <DetailValue>
                      {selectedSettlement.orders?.count ??
                        selectedSettlement.orders?.list?.length ??
                        0}{" "}
                      orders
                    </DetailValue>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>Transaction Details</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Settlement ID</DetailLabel>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <DetailValueMono>
                        {selectedSettlement.settlementId}
                      </DetailValueMono>
                      <CopyButton
                        onClick={(e) =>
                          handleCopy(selectedSettlement.settlementId, e)
                        }
                      >
                        <ContentCopy sx={{ fontSize: 10 }} />
                        Copy
                      </CopyButton>
                    </div>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>UTR Number</DetailLabel>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <DetailValueMono>
                        {selectedSettlement.utr || "—"}
                      </DetailValueMono>
                      {selectedSettlement.utr && (
                        <CopyButton
                          onClick={(e) => handleCopy(selectedSettlement.utr, e)}
                        >
                          <ContentCopy sx={{ fontSize: 10 }} />
                          Copy
                        </CopyButton>
                      )}
                    </div>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>Amount Breakdown</SectionTitle>
                <AmountCard>
                  <AmountRow>
                    <AmountLabel>Total Payout</AmountLabel>
                    <AmountValue>
                      {formatPrice(
                        selectedSettlement.orders?.totalPayout || 0,
                        Currency.INR
                      )}
                    </AmountValue>
                  </AmountRow>
                  <AmountRow>
                    <AmountLabel>
                      TDS ({(selectedSettlement.tdsPercent || 0) * 100}%)
                    </AmountLabel>
                    <AmountValue>
                      -
                      {formatPrice(
                        selectedSettlement.orders?.tds || 0,
                        Currency.INR
                      )}
                    </AmountValue>
                  </AmountRow>
                  <AmountRow>
                    <AmountLabel>Amount Settled</AmountLabel>
                    <AmountValueHighlight>
                      {formatPrice(
                        selectedSettlement.orders?.totalAmountAfterTds ??
                          selectedSettlement.orders?.totalPayoutAfterTds ??
                          0,
                        Currency.INR
                      )}
                    </AmountValueHighlight>
                  </AmountRow>
                </AmountCard>
              </DetailSection>
            </DrawerContent>

            <DrawerActions>
              <ActionButton
                onClick={() => handleDownloadCSV(selectedSettlement)}
                disabled={downloadingId === selectedSettlement.settlementId}
              >
                {downloadingId === selectedSettlement.settlementId ? (
                  <CircularProgress size={14} sx={{ color: colors.text }} />
                ) : (
                  <Download sx={{ fontSize: 16 }} />
                )}
                Download CSV
              </ActionButton>
              <ActionButton
                $primary
                onClick={(e) =>
                  handleResend(selectedSettlement.settlementId, e)
                }
                disabled={sendingId === selectedSettlement.settlementId}
              >
                {sendingId === selectedSettlement.settlementId ? (
                  <CircularProgress size={14} sx={{ color: "white" }} />
                ) : (
                  <Send sx={{ fontSize: 16 }} />
                )}
                Resend Notification
              </ActionButton>
            </DrawerActions>
          </>
        )}
      </Drawer>
    </PageContainer>
  );
}

export default SettlementsSettled;
